import {
  getActiveUser,
  navigate,
  sendGetRequest,
  formatDate,
  getUserStatus,
  sendPatchRequest,
  sendDeleteRequest,
  watchLiveData,
  allowedOperations

} from "../utils/utils";
import DataListWithSearch from "./component/@list/list-search";
import { useState, useEffect } from "react";
import AppRoutes from "../routing/routes";
import { exportToExcel } from "../utils/export.util";
import { Icon } from "@mui/material";
import { coreStyles } from "../theme/style";
import { useSelector } from "react-redux";

const getTableHead = () => {
  return [
    { id: "createdAt", label: "Created On", alignRight: false },
    { id: "name", label: "Name", alignRight: false },
    { id: "partnershipType", label: "Partnership", alignRight: false },
    { id: "pubKey", label: "Access Key", alignRight: false },
    { id: "served", label: "Active trials", center: false },
    { id: "apkUrl", label: "Apk Link", alignRight: false },
    { id: "active", label: "Status", alignRight: false }];
};

const startEdit = (uuid) => navigate(`${AppRoutes.users}/${AppRoutes.partner}${uuid ? `?uuid=${uuid}` : ''}`, true);


const PartnersList = () => {
  const appStyle = coreStyles();
  const loggedUser = getActiveUser();
  const [dataList, setDataList] = useState([]);
  const [loading, onLoading] = useState(false);
  const { queue } = useSelector((state) => state.queue);
  const { canEdit, canDelete, canCreate } = allowedOperations();

  const userOptions = [
    canEdit ? {
      icon: "delete",
      value: "delete",
      label: "Delete",
      onClick: (value, selected) => {
        onLoading(true);
        Promise.all(selected.map(uuid => sendDeleteRequest('partners', uuid))).then(response => {
          executeFetch();
        })
      }
    }: null,
    canEdit ? {
      icon: "edit",
      value: "edit",
      label: "Edit",
      onClick: (_value, selected) => startEdit(selected),
    }: null,
    canEdit ? {
      icon: "toggle_on",
      value: "activate",
      label: "Restore Access",
      onClick: (_value, selected) => executeUpdate(true, selected)
    }: null,
    canEdit ? {
      icon: "toggle_off",
      value: "deactivate",
      label: "Restrict Access",
      onClick: (_value, selected) => executeUpdate(false, selected),
    }: null,
    canCreate ? {
      icon: "output",
      value: "export",
      label: "Export Trial Codes",
      onClick: (_value, selected) => {
        const partners = dataList.filter(partner => selected.includes(partner.uuid));
        partners.forEach(partner => {
          const codes = partner.codes.map(code => ({
            "Code": code.code,
            "Status": code.isUsed ? "Active" : "Innactive",
            "Created At": formatDate(code.createdAt, "MMM DD, YYYY hh:mm A"),
            "Activated At": code.updatedAt && code.updatedAt !== code.createdAt ? formatDate(code.activatedAt, "MMM DD, YYYY hh:mm A") : "",
          }));
          exportToExcel(codes, `${partner.name} codes.xlsx`.replace(/\s/g, "_"));
        })
      },
    }: null,
    canCreate ? {
      icon: "key",
      value: "generate",
      label: "Regenerate Key",
      onClick: (_value, selected) => executeGenerate(selected, "generate-key")
    }: null,
    canCreate ? {
      icon: "room_preferences",
      value: "generate",
      label: "Generate APK",
      onClick: (_value, selected) => executeGenerate(selected, "generate-apk")
    }: null,
    {
      icon: "content_copy",
      value: "copy",
      label: "Copy Access Key",
      onClick: (_value, selected) => {
        const partners = dataList.filter(partner => selected.includes(partner.uuid));
        partners.forEach(partner => {
          navigator.clipboard.writeText(partner.pubKey).then(() => {
            console.log("Access key copied to clipboard");
          }).catch(err => {
            console.error("Failed to copy: ", err);
          });
        })
      }
    },
  ].filter(Boolean);
  const filterOptions = [];

  const executeFetch = (refresh = false) => {
    sendGetRequest('partners?metadata=true', (loading) => onLoading(refresh ? false : loading),
      (data) => setDataList(data));
  };

  const handleClickLink = async (apkUrl, name) => {
    if (!apkUrl || !name) {
      console.error("Missing URL or file name");
      return;
    }

    const link = document.createElement("a");
    link.href = apkUrl; // This sets the downloaded file name
    link.style.display = "none";

    document.body.appendChild(link);

    // Trigger the download
    link.click();

    document.body.removeChild(link);
  }


  useEffect(() => executeFetch(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loggedUser.email]
  );

  useEffect(() => {
    if (queue) watchLiveData(queue, "partners", (_) => executeFetch(true));
    // eslint-disable-next-line
  }, [queue]);

  const executeUpdate = (active, uuids) => {
    onLoading(true);
    Promise.all(uuids.map(uuid => sendPatchRequest(
      `partners/${uuid}`,
      { active })))
      .then(res => {
        executeFetch()
        onLoading(false);
      });
  };


  const executeGenerate = (uuids, path) => {
    onLoading(true);
    Promise.all(uuids.map(uuid => sendPatchRequest(
      `partners/${uuid}/${path}`, {})))
      .then(res => {
        executeFetch()
        onLoading(false);
      });
  };

  return (
    <div>
      <DataListWithSearch
        sectionTitle="Partners"
        createNewLabel={loggedUser.active && canCreate ? "Add partner" : null}
        data={dataList}
        primaryKey="uuid"
        filterColumn={"name"}
        emptyIcon="handshake"
        avatarKey="uuid"
        loading={loading}
        onCreateNewItem={() => startEdit()}
        headers={getTableHead()}
        onBack={() =>
          navigate(`${AppRoutes.contents}`, true)
        }
        rowData={(data) => {
          const partnership = data.partnershipType === "code" ? "Free Trials" :
            data.partnershipType === "core" ? "Default App" :
              data.partnershipType === "content" ? "Data Sharing" : "Data Sync";
          const canGenerateApk = ["code", "data", "core"].includes(data.partnershipType)
          const _entries = [
            { value: formatDate(data.createdAt) },
            { value: data.name },
            { value: partnership },
            { value: `${`${data.pubKey}`.substring(0, 8)} - **** - ****` },
            { value: data.partnershipType === "code" ? `${data.activeCodes.toString()}/${data.numberOfCodes.toString()}` : "N/A", align: "center" },
            {
              value: canGenerateApk ?
                `${data.apkUrl}`.startsWith('https') ? (<Icon children={"get_app"} color={"primary"} className={appStyle.asLink} onClick={() => handleClickLink(data.apkUrl, data.name)} />) : (
                  data.apkUrl
                ) : "N/A",
              align: "center",
            },
            { ...getUserStatus(data.active) },
          ];
          return {
            exclude: [
              !data.active ? "generate" : "",
              !data.active ? "deactivate" : "activate",
              !data.active || data.partnershipType === "code" ? "copy" : "",
              data.partnershipType === "data" ? "export" : "",
              canGenerateApk ? "" : "generate",
            ],
            entries: _entries,
          };
        }}
        selectionOptions={userOptions.filter((option) => option.value !== "edit")}
        filterOptions={filterOptions}
        optionItems={userOptions}
      />

    </div>
  );
};

export default PartnersList;